/**
 * Subscription Hooks
 * Handles all subscription-related operations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthReady } from '@/hooks/useAuthReady';
import {
  calculatePrice,
  createCheckoutSession,
  getCustomerPortalUrl,
  getBillingHistory,
  checkCanAddUnit,
  checkCanAddProperty,
  PRICING_TIERS,
  type CheckoutResult,
  type PricingTierId,
} from '@/lib/stripe';
import { getSupabase } from '@/lib/supabase';
import { BillingError } from '@/lib/billingErrors';

// Query keys
export const subscriptionKeys = {
  all: ['subscription'] as const,
  billing: (userId: string) => [...subscriptionKeys.all, 'billing', userId] as const,
  history: (userId: string) => [...subscriptionKeys.all, 'history', userId] as const,
  canAddUnit: (userId: string) => [...subscriptionKeys.all, 'canAddUnit', userId] as const,
  canAddProperty: (userId: string) => [...subscriptionKeys.all, 'canAddProperty', userId] as const,
};

// Types

/**
 * Subscription lifecycle states (profiles.subscription_status).
 *
 *   active            — paying (Stripe, incl. trialing) or a gift / beta /
 *                       admin plan whose end date hasn't passed.
 *   cancel_scheduled  — Stripe renewal turned off; paid until
 *                       current_period_end. Full access.
 *   past_due          — Stripe charge failed, retries in flight. Full access.
 *   cancelled         — plan ended; 30 days of grace (grace_until): the host
 *                       sees and edits everything, guest portals keep working,
 *                       nothing new can be created.
 *   suspended         — grace over: guest portals closed, dashboard only
 *                       Billing / Account / Support. Data is kept.
 *   expired           — legacy; the daily job turns it into suspended.
 *   free              — never paid (or a running trial: trial_ends_at).
 */
export type SubscriptionStatus =
  | 'free'
  | 'active'
  | 'cancel_scheduled'
  | 'past_due'
  | 'cancelled'
  | 'suspended'
  | 'expired';

export type PlanSource = 'stripe' | 'beta' | 'gift' | 'trial' | 'admin' | 'free';

/** One row of get_user_billing_info (migration 20261026200000 appended the optional fields). */
export interface BillingInfo {
  subscription_status: SubscriptionStatus | string;
  subscription_plan: string;
  billing_interval: string;
  paid_units: number;
  /** Active, non-trashed units of non-trashed properties (= what the cap counts). */
  current_units: number;
  current_period_end: string | null;
  trial_ends_at: string | null;
  is_trialing: boolean;
  days_until_renewal: number | null;
  /** Cents per month for a Stripe plan; 0 otherwise. */
  monthly_equivalent: number;
  stripe_customer_id: string | null;
  role?: string; // 'admin' | 'landlord' | 'guest'
  /** profile_has_access(): may create properties / units / bookings. */
  has_access?: boolean;
  /** Guest portals open (not suspended / expired / deleted). */
  portal_open?: boolean;
  /** End of the 30-day grace after a plan ended. */
  grace_until?: string | null;
  /** Account deletion requested at… */
  deleted_at?: string | null;
  /** …and will be carried out on. */
  deletion_scheduled_for?: string | null;
  plan_source?: PlanSource | string;
  /** Stripe subscription that will renew by itself. */
  auto_renews?: boolean;
  /** Live Stripe subscription (active / cancel_scheduled / past_due). */
  has_stripe_subscription?: boolean;
  /** Unit cap used by can_add_unit. */
  max_units?: number;
  /** Early Adopter slot number held by this host (null = none). */
  ea_slot?: number | null;
  /** A scheduled plan change (downgrade / year→month at renewal). */
  pending_plan?: string | null;
  pending_interval?: string | null;
  pending_change_at?: string | null;
  /** Beta testers' end date, if the RPC returns it. */
  beta_expires_at?: string | null;
  /** Non-trashed properties the host owns (nightly job's "has data" rule). */
  property_count?: number;
}

/**
 * True if this status grants the landlord access to create/edit resources.
 * Mirrors profile_has_access() in SQL for Stripe states — keep them in sync.
 * (The dashboard uses billingAccess.ts, which also knows trials and gifts.)
 */
export function statusGrantsAccess(status: string | undefined | null): boolean {
  if (!status) return false;
  return ['active', 'cancel_scheduled', 'past_due'].includes(status);
}

/**
 * True if account is in a state where the user should see a warning banner
 * ("ends on X", "update card", "plan ended").
 */
export function isGraceStatus(status: string | undefined | null): boolean {
  if (!status) return false;
  return ['cancel_scheduled', 'past_due', 'cancelled'].includes(status);
}

export interface BillingHistoryItem {
  id: string;
  stripe_invoice_id: string;
  amount: number;
  currency: string;
  status: string;
  description: string;
  invoice_url: string | null;
  invoice_pdf: string | null;
  units_count: number | null;
  period_start: string | null;
  period_end: string | null;
  created_at: string;
}

export interface UnitLimitInfo {
  allowed: boolean;
  currentUnits: number;
  maxUnits: number;
  needsUpgrade: boolean;
  /** 'error' = the check itself failed (unknown, NOT "0 allowed"). */
  subscriptionStatus: string;
}

export interface PropertyLimitInfo {
  allowed: boolean;
  currentProperties: number;
  maxProperties: number;
  needsUpgrade: boolean;
  /** 'error' = the check itself failed (unknown, NOT "0 allowed"). */
  subscriptionStatus: string;
}

/**
 * Hook to get current user's billing info.
 *
 * NOTE (react-query v5): while auth isn't ready the query is disabled and
 * `isLoading` is FALSE — gates must wait on `!isReady || isPending`.
 */
export function useBillingInfo() {
  const { userId, isReady } = useAuthReady();

  return useQuery({
    queryKey: subscriptionKeys.billing(userId ?? ''),
    queryFn: async (): Promise<BillingInfo | null> => {
      if (!userId) return null;

      const { data, error } = await getSupabase().rpc('get_user_billing_info', {
        p_user_id: userId,
      });

      if (error) {
        console.error('Error fetching billing info:', error);
        throw error;
      }

      return (data?.[0] as BillingInfo | undefined) || null;
    },
    enabled: isReady,
    staleTime: 60 * 1000, // 1 minute
    // No row yet = the profile is still being created (first login, Clerk
    // webhook late). Look again a few times instead of showing "no plan".
    refetchInterval: (query) =>
      query.state.data === null && query.state.dataUpdateCount < 10 ? 3000 : false,
  });
}

/**
 * Hook to get billing history
 */
export function useBillingHistory() {
  const { userId, isReady } = useAuthReady();

  return useQuery({
    queryKey: subscriptionKeys.history(userId ?? ''),
    queryFn: async (): Promise<BillingHistoryItem[]> => {
      if (!userId) return [];
      return getBillingHistory(userId);
    },
    enabled: isReady,
  });
}

/**
 * Hook to check if user can add more units
 */
export function useCanAddUnit() {
  const { userId, isReady } = useAuthReady();

  return useQuery({
    queryKey: subscriptionKeys.canAddUnit(userId ?? ''),
    queryFn: async (): Promise<UnitLimitInfo> => {
      if (!userId) {
        return {
          allowed: false,
          currentUnits: 0,
          maxUnits: 0,
          needsUpgrade: true,
          subscriptionStatus: 'none',
        };
      }
      return checkCanAddUnit(userId);
    },
    enabled: isReady,
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Hook to check if user can add more properties (tier-based)
 */
export function useCanAddProperty() {
  const { userId, isReady } = useAuthReady();

  return useQuery({
    queryKey: subscriptionKeys.canAddProperty(userId ?? ''),
    queryFn: async (): Promise<PropertyLimitInfo> => {
      if (!userId) {
        return {
          allowed: false,
          currentProperties: 0,
          maxProperties: 0,
          needsUpgrade: true,
          subscriptionStatus: 'none',
        };
      }
      return checkCanAddProperty(userId);
    },
    enabled: isReady,
    staleTime: 30 * 1000, // 30 seconds
  });
}

export interface CheckoutRequest {
  tier: PricingTierId;
  interval: 'month' | 'year';
  /** Only ask what the change would cost — change nothing. */
  preview?: boolean;
  /** Drop a scheduled (pending) plan change. */
  action?: 'cancel_change';
  /** The preview's proration_date (confirm = exactly the previewed charge). */
  proration_date?: number;
  /** Where Stripe Checkout returns to. */
  returnTo?: 'billing' | 'pricing';
}

/**
 * Start a purchase or change the plan. Never throws for business outcomes:
 * the result's `kind` says what happened ('error' carries the code).
 */
export function useCreateCheckout() {
  const { userId } = useAuthReady();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ returnTo = 'billing', ...params }: CheckoutRequest): Promise<CheckoutResult> => {
      const origin = window.location.origin;
      return createCheckoutSession({
        ...params,
        successUrl: `${origin}/dashboard/billing?success=true`,
        cancelUrl: returnTo === 'pricing'
          ? `${origin}/pricing?cancelled=true`
          : `${origin}/dashboard/billing?cancelled=true`,
      });
    },
    onSuccess: (result) => {
      if (['updated', 'scheduled', 'change_cancelled', 'requires_action'].includes(result.kind)) {
        queryClient.invalidateQueries({ queryKey: subscriptionKeys.all });
        if (userId) queryClient.invalidateQueries({ queryKey: subscriptionKeys.billing(userId) });
      }
    },
  });
}

/**
 * Hook to open the Stripe customer portal (card, invoices, cancel renewal).
 * Throws BillingError — code NO_CUSTOMER when the host never paid via Stripe.
 */
export function useCustomerPortal() {
  return useMutation({
    mutationFn: async () => {
      const result = await getCustomerPortalUrl({
        returnUrl: `${window.location.origin}/dashboard/billing`,
      });

      if ('error' in result) {
        throw new BillingError(result.code, null, result.error);
      }

      return result;
    },
  });
}

/**
 * Hook to calculate price
 */
export function useCalculatePrice(units: number, interval: 'month' | 'year' = 'year') {
  return calculatePrice(units, interval);
}

/**
 * Get pricing tier table (tier-based model).
 */
export function usePricingConfig() {
  return PRICING_TIERS;
}

/**
 * Helper to determine subscription tier name based on paid units.
 * Prefer planDisplayName(subscription_plan) — the unit count can't tell
 * Early Adopter (5) from the others.
 */
export function getSubscriptionTierName(paidUnits: number): string {
  if (paidUnits <= 3) return 'Starter';
  if (paidUnits <= 5) return 'Early Adopter';
  if (paidUnits <= 6) return 'Pro';
  if (paidUnits <= 10) return 'Business';
  return 'Enterprise';
}

/**
 * Get tier ID from paid units
 */
export function getTierIdFromUnits(paidUnits: number): string {
  if (paidUnits <= 3) return 'starter';
  if (paidUnits <= 6) return 'pro';
  if (paidUnits <= 10) return 'business';
  return 'enterprise';
}
